import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "../styles/about.css";

const fallbackSections = [
  {
    section_key: "hero",
    label: "About Udyog Connect",
    title: "A community built to connect people, industries, support, and opportunity",
    body: "Udyog Connect is a community-driven platform created to help people discover the right support, ask meaningful questions, share expertise, and build practical pathways across industries.",
    is_highlight: 1,
  },
];

const About = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAboutContent = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/2.0/about-community`
        );
        setSections(res.data?.data || []);
      } catch (error) {
        console.error("Error fetching about content:", error);
        setSections(fallbackSections);
      } finally {
        setLoading(false);
      }
    };

    fetchAboutContent();
  }, []);

  const orderedSections = sections.length ? sections : fallbackSections;
  const hero = orderedSections.find((section) => section.section_key === "hero") || orderedSections[0];
  const summary =
    orderedSections.find((section) => section.section_key === "summary") || {
      label: "Community-first platform",
      title: "What Udyog Connect stands for",
      body: "A trusted bridge between people seeking guidance and people, organizations, and enterprises ready to support community growth.",
    };
  const highlights = orderedSections.filter(
    (section) =>
      !["hero", "summary"].includes(section.section_key) &&
      Number(section.is_highlight) === 1
  );
  const storySections = orderedSections.filter(
    (section) =>
      !["hero", "summary"].includes(section.section_key) &&
      Number(section.is_highlight) !== 1
  );

  const timelineItems = useMemo(
    () =>
      storySections.filter((section) =>
        ["founded", "why", "long_term_goal"].includes(section.section_key)
      ),
    [storySections]
  );

  if (loading) {
    return <div className="about-page-wrap about-loading">Loading about Udyog Connect...</div>;
  }

  return (
    <div className="about-page-wrap about-community-page">
      <div className="about-glow about-glow-one" />
      <div className="about-glow about-glow-two" />

      <section className="about-community-hero">
        <div className="about-hero-content">
          <span className="about-pill">{hero.label}</span>
          <h1>{hero.title}</h1>
          <p>{hero.body}</p>
        </div>

        <div className="about-community-summary">
          <span className="about-tag">{summary.label}</span>
          <h2>{summary.title}</h2>
          <p>{summary.body}</p>
        </div>
      </section>

      {highlights.length > 0 && (
        <section className="about-section">
          <div className="section-title-block">
            <span className="section-mini-label">Foundation</span>
            <h2>Mission, Vision, and Core Direction</h2>
          </div>

          <div className="about-community-highlight-grid">
            {highlights.map((section) => (
              <article key={section.section_key} className="about-community-card">
                <span>{section.label}</span>
                <h3>{section.title}</h3>
                <p>{section.body}</p>
              </article>
            ))}
          </div>
        </section>
      )}

      {storySections.length > 0 && (
        <section className="about-section">
          <div className="section-title-block">
            <span className="section-mini-label">Our Story</span>
            <h2>Why This Community Exists</h2>
            <p>
              Udyog Connect is designed around the simple idea that practical
              support should be discoverable, organized, and connected to real
              people.
            </p>
          </div>

          <div className="about-community-story-grid">
            {storySections.map((section) => (
              <article key={section.section_key} className="about-community-story">
                <div className="about-story-marker" />
                <div>
                  <span>{section.label}</span>
                  <h3>{section.title}</h3>
                  <p>{section.body}</p>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {timelineItems.length > 0 && (
        <section className="about-community-closing">
          <span className="section-mini-label">Long-Term View</span>
          <h2>From connection to sustained community impact</h2>
          <p>
            The community grows by listening to real needs, connecting the
            right people, and building programs that continue to serve members
            beyond one-time interactions.
          </p>
        </section>
      )}
    </div>
  );
};

export default About;
